/**
 * In-place claim panel for US-DVU-08 / D17.
 *
 * Shown only when login returns `claim_required`. Neutral copy — never echoes
 * the email or confirms account existence.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApiV2 } from '@/api/endpoints/auth';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import { PrivacyPolicyDialog } from '@/features/auth/components/PrivacyPolicyDialog';
import { TermsOfServiceDialog } from '@/features/auth/components/TermsOfServiceDialog';
import { passwordPolicySchema } from '@/features/auth/lib/register-schema';
import { RESEND_COOLDOWN_SECONDS } from '@/features/auth/lib/resend-cooldown';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import type { LoginResponse, UserSession } from '@/types/auth';

const PERSON_NAME_MESSAGE =
  'Name may only include letters, spaces, hyphens, apostrophes, and periods';

function hasForbiddenNameChars(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code === 0x3c || code === 0x3e) return true;
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function personNameField(label: string) {
  return z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(50, `${label} is too long`)
    .refine((v) => !hasForbiddenNameChars(v), { message: PERSON_NAME_MESSAGE })
    .refine((v) => /^[\p{L}\p{M}\s'.-]+$/u.test(v), {
      message: PERSON_NAME_MESSAGE,
    });
}

const claimProfileSchema = z.object({
  firstName: personNameField('First name'),
  lastName: personNameField('Last name'),
  password: passwordPolicySchema,
  termsAccepted: z.boolean().refine((value) => value === true, {
    message: 'You must accept the Terms of Service and Privacy Policy',
  }),
});

type ClaimProfileForm = z.infer<typeof claimProfileSchema>;

type PendingClaim = {
  registrationToken: string;
  mfaChallengeToken: string;
  completeIdempotencyKey: string;
  devSecret?: string;
};

type DvClaimPanelProps = {
  /** Email from the sign-in attempt — kept server-side only; never displayed. */
  email: string;
  suggestedFirstName?: string;
  suggestedLastName?: string;
  onBackToSignIn: () => void;
};

function toSession(res: LoginResponse): UserSession {
  const u = res.user;
  if (!u || !res.accessToken) {
    throw new Error('Claim response missing session tokens');
  }
  return {
    userId: u.userId,
    temporaryUsersId: null,
    username: u.email,
    email: u.email,
    token: res.accessToken,
    role: u.role ?? '',
    roleId: 0,
    companyId: u.companyId ?? 0,
    companyName: u.companyName ?? '',
    firstName: u.firstName ?? '',
    lastName: u.lastName ?? '',
    name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
    isActiveDirectory: u.authProvider === 'entra',
    isSessionActivated: true,
    isApproved: true,
    active: true,
    authProvider: u.authProvider,
    mustChangePassword: u.mustChangePassword === true,
    mustCompleteName: u.mustCompleteName === true,
    mustAcceptLegal: u.mustAcceptLegal === true,
  };
}

function startErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    if (error.response?.status === 429) {
      return 'Too many attempts. Please wait and try again.';
    }
    if (body?.error?.message) return body.error.message;
  }
  return 'Unable to continue setup. Please try again.';
}

function completeErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    const code = body?.error?.code;
    if (code === 'mfa_invalid') {
      return body?.error?.message ?? "That didn't match. Please try again.";
    }
    if (code === 'mfa_challenge_invalid') {
      return body?.error?.message ?? 'Code expired. Request a new one.';
    }
    if (body?.error?.message) return body.error.message;
  }
  return 'Unable to finish setup. Please try again.';
}

/**
 * Claim panel: name + password → OTP → session.
 */
export function DvClaimPanel({
  email,
  suggestedFirstName,
  suggestedLastName,
  onBackToSignIn,
}: DvClaimPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setUser, setTokens } = useAuthStore();
  const [pending, setPending] = useState<PendingClaim | null>(null);
  const [code, setCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ClaimProfileForm>({
    resolver: zodResolver(claimProfileSchema),
    defaultValues: {
      firstName: suggestedFirstName ?? '',
      lastName: suggestedLastName ?? '',
      password: '',
      termsAccepted: false,
    },
  });

  const firstNameValue = watch('firstName') ?? '';
  const lastNameValue = watch('lastName') ?? '';
  const passwordValue = watch('password') ?? '';
  const termsAccepted = watch('termsAccepted');

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendCooldown]);

  async function onStart(data: ClaimProfileForm) {
    setFormError(null);
    setIsStarting(true);
    try {
      const started = await authApiV2.startDvClaim({
        email,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
        termsAccepted: data.termsAccepted,
      });
      setPending({
        registrationToken: started.registrationToken,
        mfaChallengeToken: started.mfaChallengeToken,
        completeIdempotencyKey: crypto.randomUUID(),
        ...(started.devSecret ? { devSecret: started.devSecret } : {}),
      });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      setFormError(startErrorMessage(error));
    } finally {
      setIsStarting(false);
    }
  }

  async function onComplete() {
    if (!pending) return;
    setFormError(null);
    setIsCompleting(true);
    try {
      const result = await authApiV2.registerComplete(
        {
          registrationToken: pending.registrationToken,
          mfaChallengeToken: pending.mfaChallengeToken,
          code: code.trim(),
        },
        { idempotencyKey: pending.completeIdempotencyKey },
      );

      if (result.sessionPending || !result.accessToken) {
        toast.info('Account ready — please sign in with your new password.');
        onBackToSignIn();
        return;
      }

      if (isSupabaseLoginEnabled() && result.refreshToken) {
        await getSupabaseClient().auth.setSession({
          access_token: result.accessToken,
          refresh_token: result.refreshToken,
        });
      }

      const session = toSession(result);
      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken ?? '',
      });
      setUser(session);
      await queryClient.invalidateQueries();
      navigate('/drawing-vault', { replace: true });
    } catch (error) {
      setFormError(completeErrorMessage(error));
    } finally {
      setIsCompleting(false);
    }
  }

  async function onResend() {
    if (!pending || resendCooldown > 0) return;
    setIsResending(true);
    setFormError(null);
    try {
      const sent = await authApiV2.registerSendCode({
        registrationToken: pending.registrationToken,
        mfaChallengeToken: pending.mfaChallengeToken,
        channel: 'email',
      });
      setPending({
        ...pending,
        mfaChallengeToken: sent.mfaChallengeToken,
        ...(sent.devSecret ? { devSecret: sent.devSecret } : {}),
      });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      setFormError(startErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  }

  if (pending) {
    return (
      <div className="flex flex-col gap-5">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Check your email</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the verification code we sent to continue setup.
          </p>
        </div>
        {formError && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
            {formError}
          </div>
        )}
        {pending.devSecret && (
          <p className="rounded-md bg-warning-bg px-3 py-2 text-xs text-warning">
            Dev code: {pending.devSecret}
          </p>
        )}
        <div>
          <label htmlFor="claim-code" className="mb-1.5 block text-sm font-medium text-foreground">
            Verification code
          </label>
          <AuthTextField
            id="claim-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onClear={() => setCode('')}
            clearLabel="Clear code"
            disabled={isCompleting}
            placeholder="6-digit code"
          />
        </div>
        <button
          type="button"
          disabled={isCompleting || code.trim().length !== 6}
          onClick={() => void onComplete()}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isCompleting && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          )}
          {isCompleting ? 'Finishing…' : 'Finish setup'}
        </button>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <button
            type="button"
            onClick={() => void onResend()}
            disabled={isResending || resendCooldown > 0}
            className="hover:text-foreground disabled:opacity-50"
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </button>
          <button
            type="button"
            onClick={onBackToSignIn}
            className="hover:text-foreground"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onStart)}
      className="flex flex-col gap-5"
      autoComplete="off"
    >
      <div>
        <h2 className="text-lg font-semibold text-foreground">Finish setting up your account</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Verify your email and choose a password to access Drawing Vault.
        </p>
      </div>

      {formError && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
          {formError}
        </div>
      )}

      <div>
        <label htmlFor="claim-first" className="mb-1.5 block text-sm font-medium text-foreground">
          First name
        </label>
        <AuthTextField
          {...register('firstName')}
          id="claim-first"
          value={firstNameValue}
          onClear={() => setValue('firstName', '', { shouldValidate: true })}
          clearLabel="Clear first name"
          disabled={isStarting}
          isInvalid={!!errors.firstName}
        />
        {errors.firstName && (
          <p className="mt-1 text-xs text-destructive-text">{errors.firstName.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="claim-last" className="mb-1.5 block text-sm font-medium text-foreground">
          Last name
        </label>
        <AuthTextField
          {...register('lastName')}
          id="claim-last"
          value={lastNameValue}
          onClear={() => setValue('lastName', '', { shouldValidate: true })}
          clearLabel="Clear last name"
          disabled={isStarting}
          isInvalid={!!errors.lastName}
        />
        {errors.lastName && (
          <p className="mt-1 text-xs text-destructive-text">{errors.lastName.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="claim-password" className="mb-1.5 block text-sm font-medium text-foreground">
          Password
        </label>
        <AuthTextField
          {...register('password')}
          id="claim-password"
          autoComplete="new-password"
          value={passwordValue}
          onClear={() => setValue('password', '', { shouldValidate: true })}
          clearLabel="Clear password"
          disabled={isStarting}
          passwordToggle
          isPasswordVisible={showPassword}
          onTogglePasswordVisibility={() => setShowPassword((v) => !v)}
          isInvalid={!!errors.password}
        />
        {errors.password && (
          <p className="mt-1 text-xs text-destructive-text">{errors.password.message}</p>
        )}
      </div>

      <label className="flex items-start gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          className="mt-1"
          checked={termsAccepted}
          onChange={(e) =>
            setValue('termsAccepted', e.target.checked, { shouldValidate: true })
          }
          disabled={isStarting}
        />
        <span>
          I accept the{' '}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setTermsOpen(true)}
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setPrivacyOpen(true)}
          >
            Privacy Policy
          </button>
        </span>
      </label>
      {errors.termsAccepted && (
        <p className="text-xs text-destructive-text">{errors.termsAccepted.message}</p>
      )}

      <button
        type="submit"
        disabled={isStarting}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
      >
        {isStarting && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
        )}
        {isStarting ? 'Sending code…' : 'Continue'}
      </button>

      <button
        type="button"
        onClick={onBackToSignIn}
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        Back to sign in
      </button>

      <TermsOfServiceDialog open={termsOpen} onOpenChange={setTermsOpen} />
      <PrivacyPolicyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} />
    </form>
  );
}
